/**
 * SelectionMark.ts
 *
 * This custom-extension defines the SelectionMark extension for the TipTap editor.
 * It allows wrapping selected text with a custom mark that includes a comment ID.
 *
 * Based on the Highlight extension from TipTap:
 * https://github.com/ueberdosis/tiptap/blob/develop/packages/extension-highlight/src/highlight.ts
 *
 */

import { Mark, mergeAttributes } from '@tiptap/core';
import { COMMENTS_HTML_ATTRIBUTES, SELECTION_MARK_NAME } from '../../constants';
import { TMap } from '@shared/types';

export interface SelectionMarkOptions {
  HTMLAttributes: TMap<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    'selection-mark': {
      wrapSelectionMark: (commentId: string) => ReturnType;
    };
  }
}

const SelectionMark = Mark.create<SelectionMarkOptions>({
  name: SELECTION_MARK_NAME,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute(COMMENTS_HTML_ATTRIBUTES.COMMENT_ID),
        renderHTML: (attributes) => ({ [COMMENTS_HTML_ATTRIBUTES.COMMENT_ID]: attributes.commentId }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'selection-mark',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['selection-mark', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      wrapSelectionMark:
        (commentId) =>
        ({ commands }) => {
          return commands.setMark(this.name, { commentId });
        },
    };
  },
});

export default SelectionMark;
